import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

import { toDate } from "../event-experience/agenda-schedule";

import {
  type AnnouncementDeletion,
  deletionFieldsSchema,
  deletionOf,
  mayDelete,
} from "./announcement-deletion";
import { formatSendTime } from "./scheduled-announcements";

/**
 * Announcements sent with Send now, as the organiser sees them, and the
 * "Delete for everyone" state both lists share.
 *
 * A sent announcement is its SEND_EVENT_ANNOUNCEMENT command. The ones the
 * scheduler filed for a booking carry `scheduledAnnouncementId` and are
 * listed with their booking instead. DELETE_EVENT_ANNOUNCEMENT stamps the
 * command (and the booking) with the deletion fields read here
 * (functions/python/app/application/service/event_announcement_deletion.py).
 */

/** "Deleted for everyone by Ada Admin on Thu 24 Sep 2026, 19:05 Berlin time." */
export function describeDeletion(
  deletion: Extract<AnnouncementDeletion, { state: "deleted" }>,
  timeZone: string | undefined,
): string {
  const who = deletion.byName ? ` by ${deletion.byName}` : "";
  const when = deletion.at
    ? ` on ${formatSendTime(deletion.at, timeZone)}`
    : "";
  return `Deleted for everyone${who}${when}.`;
}

export const SEND_STATUSES = ["REQUESTED", "SUCCEEDED", "FAILED"] as const;

export type SentAnnouncement = {
  id: string;
  title: string;
  body: string;
  status: (typeof SEND_STATUSES)[number];
  requestedAt: Date | null;
  recipientCount?: number;
  error?: string;
  feedbackFormId?: string;
  deletion: AnnouncementDeletion;
};

const sentCommandSchema = deletionFieldsSchema.extend({
  type: z.literal("SEND_EVENT_ANNOUNCEMENT"),
  status: z.enum(SEND_STATUSES),
  requestedAt: z.any(),
  payload: z.object({
    title: z.string(),
    body: z.string(),
    feedbackFormId: z.string().optional(),
  }),
  recipientCount: z.number().optional(),
  error: z.string().optional(),
  scheduledAnnouncementId: z.string().optional(),
});

/**
 * Whether a sent announcement can still be deleted for everyone, or retried.
 *
 * Every status: a FAILED send may have written copies before it failed (the
 * recipient count is checkpointed only every 200 members), and deleting one
 * that reached nobody costs a run that finds nothing.
 */
export function canDeleteSent(item: SentAnnouncement): boolean {
  return mayDelete(item.deletion);
}

export const PAGE_SIZE = 20;

/**
 * The newest sends, PAGE_SIZE more per loadMore().
 *
 * Scheduled sends share the query and are dropped here, because no stored
 * field tells a Send-now command apart that Firestore can filter on (the
 * scheduler adds `scheduledAnnouncementId`; a missing field matches no
 * filter). Paging keeps every older Send-now announcement reachable however
 * many scheduled sends sit between them: `hasMore` stays true while the last
 * read filled its limit, even when that read showed nothing new.
 */
export function useSentAnnouncements(eventId: string): {
  items: SentAnnouncement[];
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
} {
  const [items, setItems] = useState<SentAnnouncement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const loadMore = useCallback(() => setPages((count) => count + 1), []);

  useEffect(() => {
    // Served by admin_commands_by_type_newest_first in
    // infrastructure/gcp/terraform/firestore.tf.
    return onSnapshot(
      query(
        collection(db, "events", eventId, "admin_commands"),
        where("type", "==", "SEND_EVENT_ANNOUNCEMENT"),
        orderBy("requestedAt", "desc"),
        limit(PAGE_SIZE * pages),
      ),
      (snapshot) => {
        setHasMore(snapshot.size >= PAGE_SIZE * pages);
        const parsed: SentAnnouncement[] = [];
        for (const doc of snapshot.docs) {
          const result = sentCommandSchema.safeParse(doc.data());
          if (!result.success || result.data.scheduledAnnouncementId) continue;
          parsed.push({
            id: doc.id,
            title: result.data.payload.title,
            body: result.data.payload.body,
            status: result.data.status,
            requestedAt: toDate(result.data.requestedAt),
            recipientCount: result.data.recipientCount,
            error: result.data.error,
            feedbackFormId: result.data.payload.feedbackFormId,
            deletion: deletionOf(result.data),
          });
        }
        setItems(parsed);
        setError(null);
      },
      (err) => setError(err.message),
    );
  }, [eventId, pages]);

  return { items, error, hasMore, loadMore };
}
